// Tiny wrapper around TanStack Query's `useInfiniteQuery` that hides
// the cursor bookkeeping from page code.
//
// Backend pagination contract (Plan 02): every paginated /r/admin/*
// endpoint returns `{ success: true, data: T[], next_cursor: string }`
// where `next_cursor === ''` signals the final page. `fetchPage`
// receives the cursor verbatim (empty string for the first page) and
// MUST resolve to the full envelope — pulling `data` and `next_cursor`
// out of the envelope is what this hook does for you.

import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { PaginatedEnvelope } from '../api/types'

const NO_MORE = ''

export interface UseCursorPaginatedQueryArgs<T> {
  queryKey: QueryKey
  fetchPage: (cursor: string) => Promise<PaginatedEnvelope<T>>
  enabled?: boolean
  refetchInterval?: number | false
}

export interface UseCursorPaginatedQueryResult<T> {
  pages: T[][]
  flatItems: T[]
  cursors: string[]
  hasMore: boolean
  isLoading: boolean
  isFetchingMore: boolean
  isError: boolean
  error: Error | null
  loadMore: () => Promise<unknown>
  refresh: () => Promise<unknown>
}

export function useCursorPaginatedQuery<T>(
  args: UseCursorPaginatedQueryArgs<T>,
): UseCursorPaginatedQueryResult<T> {
  const query = useInfiniteQuery<
    PaginatedEnvelope<T>, Error, PaginatedEnvelope<T>, QueryKey, string
  >({
    queryKey: args.queryKey,
    initialPageParam: '',
    queryFn: ({ pageParam }) => args.fetchPage(pageParam ?? ''),
    getNextPageParam: (lastPage) =>
      lastPage.next_cursor && lastPage.next_cursor !== NO_MORE
        ? lastPage.next_cursor
        : undefined,
    enabled: args.enabled,
    refetchInterval: args.refetchInterval,
  })

  const data = query.data as
    | { pages: PaginatedEnvelope<T>[]; pageParams: string[] }
    | undefined

  const pages = useMemo<T[][]>(
    () => data?.pages.map((page) => page.data) ?? [],
    [data],
  )
  const flatItems = useMemo<T[]>(() => pages.flat(), [pages])
  const cursors = useMemo<string[]>(
    () => data?.pages.map((page) => page.next_cursor) ?? [],
    [data],
  )

  return {
    pages,
    flatItems,
    cursors,
    hasMore: Boolean(query.hasNextPage),
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    loadMore: () => query.fetchNextPage(),
    refresh: () => query.refetch(),
  }
}
